// KPI hedef / disclosure / erişim / snapshot / kontrol / katalog senkron şemaları — schemas/kpi.ts 600 satır bölmesi (TODO-KPI-114/-144, salt taşıma); `kpi.ts` barrel'i yeniden dışa açar.
import { z } from "zod";
import { PeriodType } from "./period.js";
import {
  KPI_ACCESS_ROLES,
  KPI_AGGREGATIONS,
  KPI_NODE_KINDS,
  KPI_REPORT_TABLE_LAYOUTS,
  KPI_TARGET_STATUSES,
  KPI_TARGET_TYPES,
  KPI_VALUE_STATUSES,
  KPI_VALUE_TYPES,
  KPI_WORKFLOW_STATES,
  KpiBreakdownSchema,
  type KpiTargetStatus,
  type KpiTargetType,
} from "./kpi-constants.js";
import { ClientEntitySchema } from "./kpi-sets.js";
import { KpiRestatementSchema } from "./kpi-values.js";

// ---- Hedefler (kpi_targets — K21, şema-only; UI TODO-KPI-01) ----
export const KpiTargetSchema = z.object({
  id: z.string().uuid(),
  clientId: z.string().uuid(),
  metricId: z.string().uuid(),
  entityId: z.string().uuid().nullable(),
  targetType: z.enum(KPI_TARGET_TYPES),
  baselineYear: z.number().int().nullable(),
  baselineValue: z.number().nullable(),
  targetYear: z.number().int().nullable(),
  targetValue: z.number().nullable(),
  targetChangePct: z.number().nullable(),
  scope: z.string().nullable(),
  owner: z.string().nullable(),
  status: z.enum(KPI_TARGET_STATUSES),
  note: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type KpiTarget = z.infer<typeof KpiTargetSchema>;

// ---- Hedef takibi (TODO-KPI-01) — girdi + ilerleme DTO'ları ----
export const KPI_TARGET_TYPE_LABELS: Record<KpiTargetType, string> = {
  absolute: "Mutlak değer",
  relative: "% değişim (baz yıla göre)",
  intensity: "Yoğunluk (birim başına)",
};
export const KPI_TARGET_STATUS_LABELS: Record<KpiTargetStatus, string> = {
  active: "Aktif",
  revised: "Revize edildi",
  achieved: "Ulaşıldı",
  missed: "Kaçırıldı",
};

/**
 * `PUT /kpi/sets/:setId/targets` gövdesi. `id` varsa güncelleme, yoksa yeni satır. `relative`
 * türünde `targetChangePct` + `baselineYear` zorunlu (baz değer verilmezse resolve'dan okunur);
 * `absolute`/`intensity` türünde `targetValue` zorunlu. `targetYear` her türde zorunlu.
 */
export const KpiTargetUpsertSchema = z
  .object({
    id: z.string().uuid().optional(),
    metricId: z.string().uuid(),
    entityId: z.string().uuid().nullable().optional(),
    targetType: z.enum(KPI_TARGET_TYPES).default("absolute"),
    baselineYear: z.number().int().min(1990).max(2100).nullable().optional(),
    baselineValue: z.number().finite().nullable().optional(),
    targetYear: z.number().int().min(1990).max(2100),
    targetValue: z.number().finite().nullable().optional(),
    targetChangePct: z.number().finite().min(-100).max(10_000).nullable().optional(),
    scope: z.string().trim().max(32).nullable().optional(),
    owner: z.string().trim().max(200).nullable().optional(),
    status: z.enum(KPI_TARGET_STATUSES).default("active"),
    note: z.string().trim().max(2000).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.targetType === "relative") {
      if (v.targetChangePct === null || v.targetChangePct === undefined)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["targetChangePct"],
          message: "% değişim hedefinde değişim yüzdesi zorunlu",
        });
      if (v.baselineYear === null || v.baselineYear === undefined)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["baselineYear"],
          message: "% değişim hedefinde baz yıl zorunlu",
        });
    } else if (v.targetValue === null || v.targetValue === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["targetValue"],
        message: "Hedef değer zorunlu",
      });
    }
    if (v.baselineYear !== null && v.baselineYear !== undefined && v.baselineYear >= v.targetYear) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["baselineYear"],
        message: "Baz yıl hedef yılından önce olmalı",
      });
    }
  });
export type KpiTargetUpsert = z.infer<typeof KpiTargetUpsertSchema>;

/** Hedef satırı + resolve'dan türetilen ilerleme (sunucu hesaplar, FE yalnız biçimler). */
export const KpiTargetProgressSchema = z.object({
  /** Gerçekleşen değerin alındığı yıl (hedef yılını aşmayan, verisi olan en son yıl); yoksa null. */
  currentYear: z.number().int().nullable(),
  currentValue: z.number().nullable(),
  /** Saklanan ya da resolve'dan okunan baz değer. */
  baselineValue: z.number().nullable(),
  /** `relative` türünde baz × (1 + pct/100); diğerlerinde `targetValue`. */
  effectiveTargetValue: z.number().nullable(),
  /** gerçekleşen / hedef × 100. */
  attainmentPct: z.number().nullable(),
  /** hedef − gerçekleşen (işaretli, birim cinsinden). */
  gap: z.number().nullable(),
  /** 0..1 ilerleme çubuğu (baz varsa baz→hedef yolu, yoksa oran). */
  progress: z.number().min(0).max(1).nullable(),
  /** Yöne göre (increaseIsGood) hedefe ulaşıldı mı; hesaplanamazsa null. */
  achieved: z.boolean().nullable(),
});
export type KpiTargetProgress = z.infer<typeof KpiTargetProgressSchema>;

export const KpiTargetWithProgressSchema = KpiTargetSchema.extend({
  indicatorId: z.string().uuid().nullable(),
  code: z.string(),
  label: z.string(),
  unit: z.string().nullable(),
  valueType: z.enum(KPI_VALUE_TYPES),
  increaseIsGood: z.boolean().nullable(),
  entityName: z.string().nullable(),
  progress: KpiTargetProgressSchema,
});
export type KpiTargetWithProgress = z.infer<typeof KpiTargetWithProgressSchema>;

// ---- Disclosure hazırlık (TODO-KPI-08 — kpi_metric_requirements × doluluk) ----
export const KPI_DISCLOSURE_ROW_STATUSES = [
  "filled",
  "pending_xxx",
  "empty",
  "not_measured",
  "calc_error",
  "not_in_set",
] as const;
export type KpiDisclosureRowStatus = (typeof KPI_DISCLOSURE_ROW_STATUSES)[number];
export const KPI_DISCLOSURE_ROW_STATUS_LABELS: Record<KpiDisclosureRowStatus, string> = {
  filled: "Dolu",
  pending_xxx: "Değer bekleniyor",
  empty: "Boş",
  not_measured: "Ölçülmüyor",
  calc_error: "Hesaplanamadı",
  not_in_set: "Veri setinde yok",
};

export const KpiDisclosureRowSchema = z.object({
  metricId: z.string().uuid(),
  /** Sette varsa set göstergesi kimliği (satıra tıkla → Veri Girişi). */
  indicatorId: z.string().uuid().nullable(),
  code: z.string(),
  label: z.string(),
  unit: z.string().nullable(),
  valueType: z.enum(KPI_VALUE_TYPES),
  /** Katalogdaki üst kategori etiketi (varsa). */
  parentLabel: z.string().nullable(),
  standard: z.string(),
  standardCode: z.string(),
  mappingType: z.string(),
  note: z.string().nullable(),
  status: z.enum(KPI_DISCLOSURE_ROW_STATUSES),
  value: z.number().optional(),
  text: z.string().optional(),
});
export type KpiDisclosureRow = z.infer<typeof KpiDisclosureRowSchema>;

export const KpiDisclosureSummarySchema = z.object({
  standard: z.string(),
  year: z.number().int(),
  /** Katalogda eşlemesi olan standartlar (seçici için). */
  standards: z.array(z.string()),
  /** Gereken TEKİL gösterge sayısı (aynı gösterge birden çok kodla eşleşebilir). */
  required: z.number().int(),
  inSet: z.number().int(),
  filled: z.number().int(),
  notMeasured: z.number().int(),
  /** XXX + boş + hesaplanamadı (sette olup dolu olmayan). */
  missing: z.number().int(),
  missingFromSet: z.number().int(),
  /** (filled + notMeasured) / required × 100 — tam sayıya yuvarlanır; required 0 → 0. */
  readyPct: z.number().int(),
  rows: z.array(KpiDisclosureRowSchema),
});
export type KpiDisclosureSummary = z.infer<typeof KpiDisclosureSummarySchema>;

// ---- Müşteri kullanıcısı set erişimi (kpi_set_access — K13) ----
export const KpiSetAccessSchema = z.object({
  id: z.string().uuid(),
  setId: z.string().uuid(),
  userId: z.string().uuid(),
  /** null = tüm kategoriler. */
  categoriesAllowed: z.array(z.string().uuid()).nullable(),
  role: z.enum(KPI_ACCESS_ROLES),
  grantedBy: z.string().uuid().nullable(),
  grantedAt: z.string(),
});
export type KpiSetAccess = z.infer<typeof KpiSetAccessSchema>;

export const KpiSetAccessGrantSchema = z.object({
  setId: z.string().uuid(),
  categoriesAllowed: z.array(z.string().uuid()).nullable().optional(),
  /** Verilmezse `owner`. */
  role: z.enum(KPI_ACCESS_ROLES).optional(),
});
export type KpiSetAccessGrant = z.infer<typeof KpiSetAccessGrantSchema>;

// ---- Snapshot manifesti (C5: DOCX manifestten üretilir, proje snapshot'ında donar) ----
export const KpiSnapshotIndicatorSchema = z.object({
  metricId: z.string().uuid(),
  code: z.string(),
  label: z.string(),
  unit: z.string().nullable(),
  parentId: z.string().uuid().nullable(),
  orderIndex: z.number().int(),
  isDerived: z.boolean(),
  activeFormula: z
    .object({ id: z.string().uuid(), label: z.string(), expression: z.string() })
    .nullable(),
  aggregation: z.enum(KPI_AGGREGATIONS),
  /** TODO-KPI-32: yalnız dolu iken yazılır — eski manifestler byte-eşit kalır (Codex R2-6). */
  aggregationWeightMetricId: z.string().uuid().optional(),
  showChange: z.boolean(),
  increaseIsGood: z.boolean().nullable(),
  footnote: z.string().nullable(),
  depth: z.number().int().nonnegative(),
  /** Kırılım (spec §3.1): yalnız `metric` dışı düğümde yazılır — kırılımsız manifest byte-eşit. */
  nodeKind: z.enum(KPI_NODE_KINDS).optional(),
  /**
   * Grup düğümünde; `memberCodes` = müşteri kapsamlı beklenen üye kodları (R15 — sette olmayan
   * beklenen üye de listededir; manifest Excel'i eksik üyeyi buradan bilir).
   */
  breakdown: KpiBreakdownSchema.extend({ memberCodes: z.array(z.string()).optional() }).optional(),
  /** Kırılım üyesinde boyut üyesi slug'ı. */
  dimensionMember: z.string().optional(),
  /**
   * Her kırılımlı toplamda yazılır — sette grup çocuğu olan toplam (formüldeki `TOPLA_ALT()` dahil)
   * ve kırılımdan türetilmiş (`catalogDerived:false`, grubu bu sette olmasa da) toplam: bu sette
   * SEÇİLEN hesap grubunun metrik id'si; null = bu sette hesap grubu yok. Manifest Excel'i hesap
   * grubunu buradan okur; örtük `TOPLA_ALT()`/`ORT_ALT()` yalnız `catalogDerived:false` toplamda.
   */
  calculationGroupMetricId: z.string().uuid().nullable().optional(),
  /**
   * Yalnız `false` yazılır: toplam katalogda HAM, kırılımdan (hesap grubu) türetilmiş — `isDerived`
   * etkin değerdir. Manifest Excel'i formülsüz toplamı örtük formülle yeniden hesaplar.
   */
  catalogDerived: z.literal(false).optional(),
});
export type KpiSnapshotIndicator = z.infer<typeof KpiSnapshotIndicatorSchema>;

export const KpiSnapshotValueSchema = z.object({
  metricId: z.string().uuid(),
  entityId: z.string().uuid(),
  year: z.number().int(),
  periodType: PeriodType,
  status: z.enum(KPI_VALUE_STATUSES),
  value: z.number().optional(),
  text: z.string().optional(),
  version: z.number().int(),
  isReported: z.boolean(),
  pinned: z.boolean(),
  workflowState: z.enum(KPI_WORKFLOW_STATES),
  unverified: z.boolean(),
  /** K11 — hücre dipnotu; yalnız doluysa yazılır (eski manifestler byte-eşit, numaralar donar). */
  footnote: z.string().optional(),
});
export type KpiSnapshotValue = z.infer<typeof KpiSnapshotValueSchema>;

export const KpiSnapshotManifestSchema = z.object({
  setId: z.string().uuid(),
  generatedAt: z.string(),
  /** TODO-KPI-29: setin çerçeve adı (DOCX "Çerçeve:" satırı). Opsiyonel — eski manifestler geçerli. */
  frameworkName: z.string().optional(),
  layout: z.enum(KPI_REPORT_TABLE_LAYOUTS),
  entities: z.array(ClientEntitySchema),
  indicators: z.array(KpiSnapshotIndicatorSchema),
  values: z.array(KpiSnapshotValueSchema),
  /** ONCEKI_YIL penceresi (Eng 2A): min(years)-1 yılı yalnız burada. */
  prevYearValues: z.array(KpiSnapshotValueSchema),
  /** M17: yalnız boş değilse yazılır — eski manifestler byte-eşit kalır. */
  restatements: z.array(KpiRestatementSchema).optional(),
  /** Birim değişimleri — yalnız boş değilse yazılır (altın kayıt byte-eşit). */
  unitChanges: z
    .array(
      z.object({
        metricId: z.string().uuid(),
        code: z.string(),
        label: z.string(),
        from: z.string().nullable(),
        to: z.string().nullable(),
        count: z.number(),
        factor: z.number().nullable(),
        changedAt: z.string(),
      }),
    )
    .optional(),
});
export type KpiSnapshotManifest = z.infer<typeof KpiSnapshotManifestSchema>;

// ---- T13/T21: bölüm metni ↔ KPI tablosu tutarlılık uyarıları (review DTO'sunda canlı hesaplanır) ----
export const KpiTextWarningSchema = z.object({
  indicatorCode: z.string(),
  indicatorLabel: z.string(),
  /** Metinde geçen sayı (birincil okuma). */
  textValue: z.number(),
  /** Tablodaki en yakın dolu değer. */
  tableValue: z.number(),
  /** Metindeki ham ifade ("48.200"). */
  raw: z.string(),
  /** Bölüm metninde başlangıç konumu. */
  sectionIndex: z.number().int().nonnegative(),
  /**
   * TODO-KPI-27: `unitNote` = metindeki sayı başka birimde yazılmış ama çevrimle tabloya eşdeğer
   * (800.596 MWh ↔ 2.882.145,6 GJ) — çelişki değil, bilgi satırı. Yoksa/`warning` = çelişki adayı.
   */
  kind: z.enum(["warning", "unitNote"]).optional(),
  /** `unitNote` açıklaması: "birim farklı (MWh ↔ GJ)". */
  note: z.string().optional(),
});
export type KpiTextWarning = z.infer<typeof KpiTextWarningSchema>;

// ---- T21: form yanıtı ↔ KPI değeri tutarlılığı (K15, C8) ----
export const KpiFormMismatchSchema = z.object({
  projectId: z.string().uuid(),
  questionCode: z.string(),
  questionLabel: z.string(),
  metricId: z.string().uuid(),
  entityId: z.string().uuid(),
  indicatorCode: z.string(),
  indicatorLabel: z.string(),
  unit: z.string().nullable(),
  year: z.number().int(),
  /** Form yanıtındaki sayı (yoksa null — yalnız KPI dolu). */
  formValue: z.number().nullable(),
  /** KPI tablosundaki dolu değer (yoksa null — yalnız form dolu). */
  kpiValue: z.number().nullable(),
  /**
   * Finar 14.4 (K4): `unit_mismatch` = soru ile gösterge birimi/tipi uyuşmuyor (kişi ↔ %) —
   * öneri YOK, bağ Set Yönetimi'nde düzeltilmeli. Eski istemciler için varsayılan `mismatch`.
   */
  kind: z.enum(["mismatch", "unit_mismatch"]).default("mismatch"),
  /** KPI değerinin SORU biriminde karşılığı (aynı aile farklı ölçekte çevrilmiş); unit_mismatch'te null. */
  suggestedValue: z.number().nullable().default(null),
  /** `suggestedValue` birim çevrimiyle elde edildi (1.500 MWh → 1.500.000 kWh). */
  converted: z.boolean().default(false),
  /** Sorunun birimi (numeric `options.unit`; percent tipi "%"); yoksa null. */
  questionUnit: z.string().nullable().default(null),
  /** Bağlı sorunun tipi (final review m3): `percent` sorusuna KPI'dan tek sayı yazılamaz (dağılım)
   * → sunucu `suggestedValue=null` verir, FE "KPI'dan forma yaz"ı göstermez. */
  questionType: z.string().default("numeric"),
});
export type KpiFormMismatch = z.infer<typeof KpiFormMismatchSchema>;

// --- TODO-KPI-03: katalog → set bildirimli aktarım ---------------------------------------------
// Set = üyelik; etiket/birim/aggregation override'ları ve formül KOPYASI katalog değişince sessiz
// kalır. `KpiCatalogChange` = bir setin katalogdan ayrıştığı tek nokta; `new_metric` = kategorisi
// sette olan ama sete eklenmemiş aktif katalog göstergesi. Uygula: override'ı katalog değerine çek
// (override null → katalogdan akar) / formül listesini varsayılanla değiştir / yeni göstergeyi ekle.

export const KPI_CATALOG_CHANGE_KINDS = [
  "label",
  "unit",
  "formula",
  "aggregation",
  "new_metric",
  "package_new_metric",
  "package_removed_metric",
] as const;
export type KpiCatalogChangeKind = (typeof KPI_CATALOG_CHANGE_KINDS)[number];

export const KpiCatalogChangeSchema = z.object({
  metricId: z.string().uuid(),
  metricCode: z.string(),
  metricLabel: z.string(),
  kind: z.enum(KPI_CATALOG_CHANGE_KINDS),
  /** Katalogdaki değer (formülde aktif varsayılan ifade; yeni göstergede etiket). */
  catalogValue: z.string().nullable(),
  /** Setteki değer (override / aktif set formülü); yeni göstergede null. */
  setValue: z.string().nullable(),
  /** Mevcut set göstergesi (yeni göstergede null). */
  indicatorId: z.string().uuid().nullable(),
  /** Yeni gösterge: sete eklenecek üst düğüm (set göstergesi) + etiketi. */
  parentIndicatorId: z.string().uuid().nullable(),
  parentLabel: z.string().nullable(),
  /** Paket kaynaklı değişiklikte paket (paket dışı türlerde null/yok). */
  packageId: z.string().uuid().nullable().optional(),
  packageName: z.string().nullable().optional(),
  /** yok = eyleme açık (eski türler alanı yaymaz; paket türleri T5'te açıkça yazar). Uygulanabilir mi (ör. ataları sette eksik paket kalemi değil). */
  actionable: z.boolean().optional(),
  /** Eksik ata sayısı (actionable=false gerekçesi). */
  ancestorsMissing: z.number().int().optional(),
});
export type KpiCatalogChange = z.infer<typeof KpiCatalogChangeSchema>;

export const KpiCatalogSyncStatusSchema = z.object({
  setId: z.string().uuid(),
  changes: z.array(KpiCatalogChangeSchema),
  /** O anki fark listesinin kararlı imzası — FE "hepsini yoksay" bunu saklar (localStorage). */
  signature: z.string(),
});
export type KpiCatalogSyncStatus = z.infer<typeof KpiCatalogSyncStatusSchema>;

export const KpiCatalogSyncApplyBodySchema = z.object({
  items: z
    .array(z.object({ metricId: z.string().uuid(), kind: z.enum(KPI_CATALOG_CHANGE_KINDS) }))
    .min(1)
    .max(500),
});
export type KpiCatalogSyncApplyBody = z.infer<typeof KpiCatalogSyncApplyBodySchema>;

export const KpiCatalogSyncApplyResultSchema = z.object({
  applied: z.number().int().nonnegative(),
  /** Paket kalemleriyle birlikte sete eklenen ata (kategori) sayısı — "atalarıyla" sonuç satırı. */
  ancestorsAdded: z.number().int().nonnegative().optional(),
  failed: z.array(
    z.object({
      metricId: z.string().uuid(),
      kind: z.enum(KPI_CATALOG_CHANGE_KINDS),
      message: z.string(),
    }),
  ),
  /** Uygulama sonrası kalan farklar (yeniden sorgu). */
  status: KpiCatalogSyncStatusSchema,
});
export type KpiCatalogSyncApplyResult = z.infer<typeof KpiCatalogSyncApplyResultSchema>;
